import React from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material'
import type { ToggleButtonProps } from '@mui/material/ToggleButton'
import type { TooltipProps } from '@mui/material/Tooltip'
import type { SelectChangeEvent } from '@mui/material/Select'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { useOutletContext } from 'react-router-dom'
import DashboardGrid from '../../components/prep/dashboard/DashboardGrid'
import MyPrepCard from '../../components/prep/dashboard/MyPrepCard'
import CategoryCard, { type PrepDashboardCategoryCard } from '../../components/prep/dashboard/CategoryCard'
import {
  type DashboardCardItemActions,
  type DashboardCardSettingsContext,
} from '../../components/prep/dashboard/types'
import { buildCategoryChipSx, buildNeutralChipSx } from '../../components/prep/dashboard/utils'
import PrepDashboardSettingsModal from '../../components/prep/dashboard/PrepDashboardSettingsModal'
import PrepDetailDrawer from '../../components/prep/PrepDetailDrawer'
import AddPrepToShowClockModal from '../../components/prep/AddPrepToShowClockModal'
import {
  fetchPrepCategories,
  fetchPrepItemDetail,
  getMyPrepList,
  type PrepDashboardCard,
  type PrepDateBasis,
  type PrepDetailResponse,
  type PrepListItem,
} from '../../api/prep'
import type { MeResponse } from '../../api/me'
import useUserSettingsStore, {
  type PrepLayout,
} from '../../store/userSettings'
import type { PrepAgeMaxDays } from '../../api/userSettings'
import usePrepListStore from '../../store/prepListStore'
import useStationStore from '../../store/station'
import usePrepDashboardStore from '../../store/prepDashboardStore'
import { useShallow } from 'zustand/react/shallow'
import { usePageTitle } from '../../components/common/PageTitleContext'
import SettingsIcon from '@mui/icons-material/Settings'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import DashboardIcon from '@mui/icons-material/Dashboard'
import LooksOneIcon from '@mui/icons-material/LooksOne'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddPrepModal from '../../components/prep/AddPrepModal'
import { updateUserSettings } from '../../api/userSettings'
import { shortenCategoryLabel } from '../../utils/categoryLabel'
import MyPrepToggleButton from '../../components/prep/MyPrepToggleButton'
import usePrepRecoveryStore from '../../store/prepRecoveryStore'
import usePrepDashboardRevisionPoll from './usePrepDashboardRevisionPoll'
import PrepDateFilters from '../../components/prep/PrepDateFilters'

interface DashboardCardSettingsDialogProps {
  open: boolean
  card: PrepDashboardCard | null
  context: DashboardCardSettingsContext | null
  onClose: () => void
  onSave: (options: {
    itemsPerPage: number
    headlineLines?: 1 | 2 | 3 | null
    listShowImages?: boolean | null
    column?: number | null
    columnOrder?: number | null
  }) => Promise<void> | void
  onRemove: () => Promise<void> | void
  columns: number
  cards: PrepDashboardCard[]
}

const clampItemsPerPage = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 5
  }
  const normalized = Math.floor(value)
  return Math.min(15, Math.max(3, normalized))
}

const isEphemeralCardUpdate = (partial: Partial<PrepDashboardCard>): boolean =>
  'page' in partial && Object.keys(partial).length === 1

interface StoredCardHandlers {
  onCardChange: (next: PrepDashboardCard) => void
  onPartialChange: (patch: Partial<PrepDashboardCard>) => void
  onItemsChange: (items: PrepListItem[], total: number) => void
  onSelectItem: (item: PrepListItem) => void
  onRegisterActions: (actions: DashboardCardItemActions | null) => void
}

interface DashboardSnapshotData {
  items: PrepListItem[]
  total: number
  loading: boolean
  refreshing: boolean
  error: string | null
  version: number
}

const EMPTY_SNAPSHOT_ITEM_IDS: number[] = []

const DashboardSnapshotConnector: React.FC<{
  cardId: string
  children: (snapshot: DashboardSnapshotData) => React.ReactNode
}> = React.memo(({ cardId, children }) => {
  const snapshotCard = usePrepDashboardStore((state) => state.cardsById[cardId])
  const itemIds = snapshotCard?.item_ids ?? EMPTY_SNAPSHOT_ITEM_IDS
  const items = usePrepDashboardStore(
    useShallow((state) =>
      itemIds.flatMap((itemId) => {
        const item = state.itemsById[itemId]
        return item ? [item] : []
      }),
    ),
  )
  const loading = usePrepDashboardStore((state) => state.loading)
  const refreshing = usePrepDashboardStore((state) => state.refreshing)
  const error = usePrepDashboardStore((state) => state.error)
  const version = usePrepDashboardStore((state) => state.version)

  return children({
    items,
    total: snapshotCard?.total ?? 0,
    loading: loading && !snapshotCard,
    refreshing,
    error: snapshotCard ? null : error,
    version,
  })
})

DashboardSnapshotConnector.displayName = 'DashboardSnapshotConnector'

type LayoutToggleButtonProps = ToggleButtonProps & {
  tooltip: string
  tooltipProps?: Partial<TooltipProps>
}

const LayoutToggleButton = React.forwardRef<HTMLButtonElement, LayoutToggleButtonProps>(
  function LayoutToggleButton({ tooltip, tooltipProps = {}, disabled, children, ...toggleButtonProps }, ref) {
    const button = (
      <ToggleButton {...toggleButtonProps} disabled={disabled} ref={ref}>
        {children}
      </ToggleButton>
    )

    if (disabled) {
      return (
        <Tooltip {...tooltipProps} title={tooltip}>
          <Box component="span" sx={{ display: 'inline-flex', width: '100%' }}>
            {button}
          </Box>
        </Tooltip>
      )
    }

    return (
      <Tooltip {...tooltipProps} title={tooltip}>
        {button}
      </Tooltip>
    )
  },
)

;(LayoutToggleButton as unknown as { muiName: string }).muiName = ToggleButton.muiName

const DashboardCardSettingsDialog: React.FC<DashboardCardSettingsDialogProps> = ({
  open,
  card,
  context,
  onClose,
  onSave,
  onRemove,
  columns,
  cards,
}) => {
  const [itemsPerPageInput, setItemsPerPageInput] = React.useState('')
  const [columnInput, setColumnInput] = React.useState('1')
  const [orderInput, setOrderInput] = React.useState('1')
  const [headlineLinesInput, setHeadlineLinesInput] = React.useState<'default' | '1' | '2' | '3'>('default')
  const [listShowImagesInput, setListShowImagesInput] = React.useState<'default' | 'on' | 'off'>('default')
  const [touched, setTouched] = React.useState(false)
  const [orderTouched, setOrderTouched] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [removing, setRemoving] = React.useState(false)
  const [confirmRemove, setConfirmRemove] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const normalizedColumns = React.useMemo(() => Math.min(4, Math.max(1, Math.floor(columns))), [columns])

  const deriveOrderOptions = React.useCallback(
    (col: number): number[] => {
      const normalizedCol = Math.min(normalizedColumns, Math.max(1, Math.floor(col)))
      const countInCol = cards.reduce((count, c, idx) => {
        const resolvedColumn = (() => {
          if (typeof c.column === 'number' && Number.isFinite(c.column)) {
            return Math.min(normalizedColumns, Math.max(1, Math.floor(c.column)))
          }
          return (idx % normalizedColumns) + 1
        })()

        if (resolvedColumn !== normalizedCol) {
          return count
        }

        if (card?.id && c.id === card.id) {
          return count
        }

        return count + 1
      }, 0)
      return Array.from({ length: countInCol + 1 }, (_unused, idx) => idx + 1)
    },
    [card?.id, cards, normalizedColumns],
  )

  React.useEffect(() => {
    if (open && card) {
      setItemsPerPageInput(String(card.itemsPerPage ?? 5))
      const currentColumn = typeof card.column === 'number' && card.column > 0 ? card.column : 1
      setColumnInput(String(currentColumn))
      const orderOptions = deriveOrderOptions(currentColumn)
      const currentOrder =
        typeof card.columnOrder === 'number' && card.columnOrder > 0
          ? Math.min(Math.max(card.columnOrder, 1), orderOptions[orderOptions.length - 1])
          : orderOptions[orderOptions.length - 1]
      setOrderInput(String(currentOrder))
      if (card.headlineLines === 1 || card.headlineLines === 2 || card.headlineLines === 3) {
        setHeadlineLinesInput(String(card.headlineLines) as '1' | '2' | '3')
      } else {
        setHeadlineLinesInput('default')
      }
      if (card.listShowImages === true) {
        setListShowImagesInput('on')
      } else if (card.listShowImages === false) {
        setListShowImagesInput('off')
      } else {
        setListShowImagesInput('default')
      }
      setTouched(false)
      setSaving(false)
      setRemoving(false)
      setConfirmRemove(false)
      setError(null)
    }
  }, [card, open])

  React.useEffect(() => {
    if (!open) {
      return
    }
    const col = Number.parseInt(columnInput, 10)
    const resolvedCol = Number.isFinite(col) ? Math.min(normalizedColumns, Math.max(1, Math.floor(col))) : 1
    const options = deriveOrderOptions(resolvedCol)
    const maxOption = options[options.length - 1] ?? 1
    const currentOrder = Number.parseInt(orderInput, 10)
    const clampedOrder = Number.isFinite(currentOrder)
      ? Math.min(Math.max(currentOrder, 1), maxOption)
      : maxOption

    const nextColumnValue = String(resolvedCol)
    const nextOrderValue = String(clampedOrder)
    const shouldResetOrderTouched = columnInput !== nextColumnValue || orderInput !== nextOrderValue

    if (columnInput !== nextColumnValue) {
      setColumnInput(nextColumnValue)
    }
    if (orderInput !== nextOrderValue) {
      setOrderInput(nextOrderValue)
    }
    if (shouldResetOrderTouched) {
      setOrderTouched(false)
    }
  }, [columnInput, deriveOrderOptions, normalizedColumns, open, orderInput])

  const numericValue = React.useMemo(() => {
    if (!open) {
      return Number.NaN
    }
    const parsed = Number.parseInt(itemsPerPageInput, 10)
    if (Number.isFinite(parsed)) {
      return parsed
    }
    return Number.NaN
  }, [itemsPerPageInput, open])

  const parsedColumn = React.useMemo(() => {
    if (!open) {
      return Number.NaN
    }
    const parsed = Number.parseInt(columnInput, 10)
    if (Number.isFinite(parsed)) {
      return parsed
    }
    return Number.NaN
  }, [columnInput, open])

  const parsedOrder = React.useMemo(() => {
    if (!open) {
      return Number.NaN
    }
    const parsed = Number.parseInt(orderInput, 10)
    if (Number.isFinite(parsed)) {
      return parsed
    }
    return Number.NaN
  }, [open, orderInput])

  const isWithinRange = Number.isFinite(numericValue) && numericValue >= 3 && numericValue <= 15
  const resolvedColumn = Number.isFinite(parsedColumn)
    ? Math.min(normalizedColumns, Math.max(1, Math.floor(parsedColumn)))
    : 1
  const orderOptions = React.useMemo(() => deriveOrderOptions(resolvedColumn), [deriveOrderOptions, resolvedColumn])
  const maxOrder = orderOptions[orderOptions.length - 1] ?? 1
  const isOrderWithinRange = Number.isFinite(parsedOrder) && parsedOrder >= 1 && parsedOrder <= maxOrder
  const showValidationError = touched && !isWithinRange
  const positionError = orderTouched && !isOrderWithinRange
  const helperText = showValidationError ? 'Enter a number between 3 and 15.' : undefined
  const positionHelperText = positionError ? `Enter a number between 1 and ${maxOrder}.` : '1 = first in column'
  const canSave = Boolean(card) && isWithinRange && !saving && !removing && !positionError

  const handleColumnChange = (event: SelectChangeEvent) => {
    const nextCol = Number(event.target.value)
    setColumnInput(String(nextCol))
    setOrderTouched(false)
  }

  const handleOrderChange = (event: SelectChangeEvent) => {
    setOrderInput(String(event.target.value))
    setOrderTouched(true)
  }

  const handleSave = async () => {
    if (!card) {
      return
    }
    setTouched(true)
    setOrderTouched(true)
    if (!isWithinRange || !Number.isFinite(numericValue) || positionError) {
      return
    }
    const normalizedItemsPerPage = clampItemsPerPage(numericValue)
    const desiredColumn = Number.isFinite(parsedColumn)
      ? Math.min(normalizedColumns, Math.max(1, parsedColumn))
      : 1
    const desiredOrder = Number.isFinite(parsedOrder) ? Math.max(1, parsedOrder) : maxOrder
    const clampedOrder = Math.min(maxOrder, desiredOrder)
    const nextHeadlineLines =
      headlineLinesInput === 'default' ? null : (Number(headlineLinesInput) as 1 | 2 | 3 | null)
    const nextListShowImages =
      listShowImagesInput === 'default'
        ? null
        : listShowImagesInput === 'on'
          ? true
          : false
    setSaving(true)
    setError(null)
    try {
      await onSave({
        itemsPerPage: normalizedItemsPerPage,
        column: desiredColumn,
        columnOrder: clampedOrder,
        headlineLines: nextHeadlineLines,
        listShowImages: nextListShowImages,
      })
      setColumnInput(String(desiredColumn))
      setOrderInput(String(clampedOrder))
      onClose()
    } catch (err) {
      setError('Could not save card settings. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async () => {
    if (!card) {
      return
    }
    if (!confirmRemove) {
      setConfirmRemove(true)
      return
    }
    setRemoving(true)
    setError(null)
    try {
      await onRemove()
      onClose()
    } catch (err) {
      setError('Could not remove this card. Please try again.')
    } finally {
      setRemoving(false)
    }
  }

  const defaultName = React.useMemo(() => {
    if (!card) {
      return ''
    }
    if (card.type === 'my_prep') {
      return 'My Prep Items'
    }
    if (card.type === 'category') {
      const provided = card.categoryName?.trim()
      if (provided) {
        return provided
      }
      return card.categoryId ? `Category #${card.categoryId}` : 'Category'
    }
    return 'Card'
  }, [card])

  const displayName = React.useMemo(() => {
    const provided = context?.name?.trim()
    if (provided) {
      return provided
    }
    return defaultName
  }, [context?.name, defaultName])

  const chipType = context?.type ?? card?.type ?? 'category'
  const chipLabel = React.useMemo(() => {
    if (!displayName) {
      return chipType === 'my_prep' ? 'My Prep Items' : 'Category'
    }
    if (chipType === 'category') {
      const shortened = shortenCategoryLabel(displayName)
      return shortened.label || displayName
    }
    return displayName
  }, [chipType, displayName])

  const chipSx = React.useMemo(
    () => (chipType === 'category' ? buildCategoryChipSx(context?.slug ?? null) : buildNeutralChipSx()),
    [chipType, context?.slug],
  )

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="dashboard-card-settings-title" fullWidth maxWidth="xs">
      <DialogTitle id="dashboard-card-settings-title">
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ minWidth: 0, flexWrap: 'wrap', rowGap: 0.75 }}
        >
          <Chip size="small" component="div" tabIndex={-1} label={chipLabel} sx={chipSx} />
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Card settings
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Items per page"
            type="number"
            value={itemsPerPageInput}
            onChange={(event) => {
              setItemsPerPageInput(event.target.value)
              setTouched(true)
            }}
            inputProps={{ min: 3, max: 15, step: 1 }}
            error={showValidationError}
            helperText={helperText}
            disabled={saving || removing}
            fullWidth
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <FormControl fullWidth size="small" disabled={saving || removing}>
              <InputLabel id="dashboard-card-column-label">Column</InputLabel>
              <Select
                labelId="dashboard-card-column-label"
                label="Column"
                value={columnInput}
                onChange={handleColumnChange}
              >
                {Array.from({ length: normalizedColumns }).map((_, idx) => (
                  <MenuItem key={idx + 1} value={String(idx + 1)}>
                    {idx + 1}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={saving || removing}>
              <InputLabel id="dashboard-card-order-label">Order</InputLabel>
              <Select
                labelId="dashboard-card-order-label"
                label="Order"
                value={orderInput}
                onChange={handleOrderChange}
                error={positionError}
              >
                {orderOptions.map((option) => (
                  <MenuItem key={option} value={String(option)}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {positionHelperText ? (
                <Typography
                  variant="caption"
                  color={positionError ? 'error' : 'text.secondary'}
                  sx={{ ml: 1, mt: 0.5 }}
                >
                  {positionHelperText}
                </Typography>
              ) : null}
            </FormControl>
          </Stack>
          <Stack direction="row" spacing={1}>
            <FormControl fullWidth size="small" disabled={saving || removing}>
              <InputLabel id="dashboard-card-lines-label">Lines</InputLabel>
              <Select
                labelId="dashboard-card-lines-label"
                value={headlineLinesInput}
                label="Lines"
                onChange={(event) => setHeadlineLinesInput(event.target.value as typeof headlineLinesInput)}
              >
                <MenuItem value="default">Default</MenuItem>
                <MenuItem value="1">1 line</MenuItem>
                <MenuItem value="2">2 lines</MenuItem>
                <MenuItem value="3">3 lines</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={saving || removing}>
              <InputLabel id="dashboard-card-images-label">Images</InputLabel>
              <Select
                labelId="dashboard-card-images-label"
                value={listShowImagesInput}
                label="Images"
                onChange={(event) => setListShowImagesInput(event.target.value as typeof listShowImagesInput)}
              >
                <MenuItem value="default">Default</MenuItem>
                <MenuItem value="on">Show</MenuItem>
                <MenuItem value="off">Hide</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Stack spacing={1}>
            <Button
              variant="contained"
              color="error"
              onClick={handleRemove}
              disabled={saving || removing}
            >
              {confirmRemove ? 'Confirm remove' : 'Remove card'}
            </Button>
            {confirmRemove ? (
              <DialogContentText component="div" sx={{ color: 'text.secondary' }}>
                Removing this card will delete it from your dashboard.
              </DialogContentText>
            ) : null}
          </Stack>
          {error ? (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving || removing}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!canSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface OutletContext {
  me: MeResponse
  includeSuperadmin: boolean
}

const PrepDashboardPage: React.FC = () => {
  usePageTitle('Prep Dashboard', { showBreadcrumbs: false })

  const optimizedDashboardEnabled =
    typeof window === 'undefined' || window.__RPA_BOOTSTRAP__?.prepDashboardOptimized !== false

  const { me, includeSuperadmin } = useOutletContext<OutletContext>()
  const dashboard = useUserSettingsStore((state) => state.prep.dashboard)
  const layout = useUserSettingsStore((state) => state.prep.layout)
  const recoveryOpen = usePrepRecoveryStore((state) => state.open)
  const setRecoveryOpen = usePrepRecoveryStore((state) => state.setOpen)
  const setPrepDashboard = useUserSettingsStore((state) => state.setPrepDashboard)
  const setPrepLayout = useUserSettingsStore((state) => state.setPrepLayout)
  const headlineLines = useUserSettingsStore((state) => state.prep.headlineLines)
  const setPrepHeadlineLines = useUserSettingsStore((state) => state.setPrepHeadlineLines)
  const ageMaxDays = useUserSettingsStore((state) => state.prep.ageMaxDays)
  const setPrepAgeMaxDays = useUserSettingsStore((state) => state.setPrepAgeMaxDays)
  const dateBasis = useUserSettingsStore((state) => state.prep.dateBasis)
  const setPrepDateBasis = useUserSettingsStore((state) => state.setPrepDateBasis)
  const listShowImages = useUserSettingsStore((state) => state.prep.listShowImages)
  const setListShowImages = useUserSettingsStore((state) => state.setListShowImages)
  const categoriesLoadedAt = usePrepListStore((state) => state.categoriesLoadedAt)
  const categoriesById = usePrepListStore((state) => state.categoriesById)
  const setCategories = usePrepListStore((state) => state.setCategories)
  const theme = useTheme()
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'))

  const setAllCardsCollapsed = React.useCallback(
    (collapsed: boolean) => {
      const {
        prep: { dashboard: currentDashboard },
      } = useUserSettingsStore.getState()

      const cards = Array.isArray(currentDashboard.cards) ? currentDashboard.cards : []
      let changed = false

      const nextCards = cards.map((card) => {
        if (card.collapsed === collapsed) {
          return card
        }
        changed = true
        return { ...card, collapsed }
      })

      if (!changed) {
        return
      }

      const nextDashboard = { ...currentDashboard, cards: nextCards }
      setPrepDashboard(nextDashboard, { persist: false })
    },
    [setPrepDashboard],
  )

  const dashboardRef = React.useRef(dashboard)
  React.useEffect(() => {
    dashboardRef.current = dashboard
  }, [dashboard])

  const cardActionsRef = React.useRef(new Map<string, DashboardCardItemActions>())
  const cardHandlerCacheRef = React.useRef(new Map<string, StoredCardHandlers>())
  const selectionRef = React.useRef<{ cardId: string | null; itemId: number | null }>({
    cardId: null,
    itemId: null,
  })
  const retainedUsedDrawerIdRef = React.useRef<number | null>(null)
  const [itemsVersion, setItemsVersion] = React.useState(0)
  const [selectedCardId, setSelectedCardId] = React.useState<string | null>(null)
  const [selectedId, setSelectedId] = React.useState<number | null>(null)
  const [selectedItem, setSelectedItem] = React.useState<PrepListItem | null>(null)
  const detailCacheRef = React.useRef(new Map<number, PrepDetailResponse>())
  const cacheDetail = React.useCallback((detail: PrepDetailResponse) => {
    const cache = detailCacheRef.current
    cache.delete(detail.id)
    cache.set(detail.id, detail)
    while (cache.size > 100) {
      const oldestId = cache.keys().next().value as number | undefined
      if (oldestId === undefined) {
        break
      }
      cache.delete(oldestId)
    }
  }, [])
  const [dashboardSettingsOpen, setDashboardSettingsOpen] = React.useState(false)
  const [settingsOpenForCardId, setSettingsOpenForCardId] = React.useState<string | null>(null)
  const [settingsContext, setSettingsContext] = React.useState<DashboardCardSettingsContext | null>(null)
  const [snackbar, setSnackbar] = React.useState<{
    message: string
    severity: 'success' | 'error' | 'info'
  } | null>(null)
  const [addPrepOpen, setAddPrepOpen] = React.useState(false)
  const stationId = useStationStore((state) => state.activeStation?.id ?? 0)
  const loadDashboardSnapshot = usePrepDashboardStore((state) => state.loadSnapshot)
  const snapshotCardsById = usePrepDashboardStore((state) => state.cardsById)
  const snapshotSource = usePrepDashboardStore((state) => state.source)
  const snapshotRefreshing = usePrepDashboardStore((state) => state.refreshing)
  const snapshotStale = usePrepDashboardStore((state) => state.stale)
  const snapshotError = usePrepDashboardStore((state) => state.error)
  const [addToShowClockTarget, setAddToShowClockTarget] = React.useState<{
    prepId: number
    prepHeadline: string
    launchedFromMyPrep: boolean
  } | null>(null)

  const handleOpenAddToShowClock = React.useCallback(
    (params: { prepId: number; prepHeadline: string; launchedFromMyPrep: boolean }) => {
      setAddToShowClockTarget(params)
    },
    [],
  )

  const modalCategories = React.useMemo(
    () =>
      Object.values(categoriesById)
        .filter((category) => typeof category.id === 'number' && category.name.trim().length > 0)
        .map((category) => ({ id: category.id, name: category.name.trim() })),
    [categoriesById],
  )

  const handleAddPrepSuccess = React.useCallback(() => {
    setAddPrepOpen(false)
    setSnackbar({ message: 'Added', severity: 'success' })
  }, [setSnackbar])

  const handleAddPrepError = React.useCallback((message: string) => {
    setSnackbar({ message, severity: 'error' })
  }, [setSnackbar])

  React.useEffect(() => {
    selectionRef.current = { cardId: selectedCardId, itemId: selectedId }
  }, [selectedCardId, selectedId])

  React.useEffect(() => {
    if (categoriesLoadedAt !== undefined) {
      return
    }

    let cancelled = false
    const controller = new AbortController()

    fetchPrepCategories({ scope: 'global', includeEmpty: true }, { signal: controller.signal })
      .then((response) => {
        if (cancelled || controller.signal.aborted) {
          return
        }

        const categories = Array.isArray(response.categories) ? response.categories : []
        setCategories(
          categories.map((category) => ({
            id: category.id,
            slug: typeof category.slug === 'string' ? category.slug.trim() : '',
            name: typeof category.name === 'string' ? category.name.trim() : '',
          })),
        )
      })
      .catch(() => {
        if (cancelled || controller.signal.aborted) {
          return
        }
        setCategories([])
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [categoriesLoadedAt, setCategories])

  const [injectedMyPrepCard, setInjectedMyPrepCard] = React.useState<PrepDashboardCard>({
    id: '__auto_my_prep__',
    type: 'my_prep',
    itemsPerPage: 5,
    collapsed: false,
    page: 1,
  })

  const handleExpandAllCards = React.useCallback(() => {
    setAllCardsCollapsed(false)
    setInjectedMyPrepCard((previous) => {
      if (!previous.collapsed) {
        return previous
      }
      return { ...previous, collapsed: false }
    })
  }, [setAllCardsCollapsed, setInjectedMyPrepCard])

  const handleCollapseAllCards = React.useCallback(() => {
    setAllCardsCollapsed(true)
    setInjectedMyPrepCard((previous) => {
      if (previous.collapsed) {
        return previous
      }
      return { ...previous, collapsed: true }
    })
  }, [setAllCardsCollapsed, setInjectedMyPrepCard])

  const isSuperadmin = React.useMemo(() => {
    const roles = new Set<string>()
    me.roles?.forEach((role) => roles.add(role))
    me.user?.roles?.forEach((role) => roles.add(role))
    return includeSuperadmin || roles.has('superadmin')
  }, [includeSuperadmin, me.roles, me.user?.roles])

  const applyCardPatch = React.useCallback(
    (cardId: string, patch: Partial<PrepDashboardCard>) => {
      if (!patch || Object.keys(patch).length === 0) {
        return
      }

      const currentDashboard = dashboardRef.current
      const currentCard = currentDashboard.cards.find((card) => card.id === cardId)
      if (!currentCard) {
        return
      }

      let hasChanges = false
      for (const [key, value] of Object.entries(patch)) {
        if ((currentCard as Record<string, unknown>)[key] !== value) {
          hasChanges = true
          break
        }
      }

      if (!hasChanges) {
        return
      }

      const nextCards = currentDashboard.cards.map((card) =>
        card.id === cardId ? { ...card, ...patch } : card,
      )
      const nextDashboard = { ...currentDashboard, cards: nextCards }
      const persistOptions = isEphemeralCardUpdate(patch) ? { persist: false } : undefined

      // Pagination is ephemeral UI state; skip persistence when only the page changes.
      setPrepDashboard(nextDashboard, persistOptions)
    },
    [setPrepDashboard, setSnackbar],
  )

  const updateCardSettings = React.useCallback(
    (cardId: string, nextCard: PrepDashboardCard) => {
      const currentCard = dashboardRef.current.cards.find((card) => card.id === cardId)
      if (!currentCard) {
        return
      }

      const diffKeys: Array<keyof PrepDashboardCard> = [
        'type',
        'categoryId',
        'categorySlug',
        'categoryName',
        'itemsPerPage',
        'collapsed',
        'page',
        'headlineLines',
        'listShowImages',
      ]

      const patch: Partial<PrepDashboardCard> = {}
      for (const key of diffKeys) {
        const nextValue = nextCard[key]
        if (currentCard[key] !== nextValue) {
          patch[key] = nextValue as PrepDashboardCard[keyof PrepDashboardCard]
        }
      }

      applyCardPatch(cardId, patch)
    },
    [applyCardPatch],
  )

  const handleRegisterActions = React.useCallback(
    (cardId: string, actions: DashboardCardItemActions | null) => {
      const map = cardActionsRef.current
      if (actions) {
        map.set(cardId, actions)
        const { cardId: activeCardId, itemId } = selectionRef.current
        if (itemId && activeCardId === cardId) {
          const updated = actions.getItemById(itemId)
          if (updated) {
            setSelectedItem(updated)
          } else if (retainedUsedDrawerIdRef.current !== itemId) {
            selectionRef.current = { cardId: selectionRef.current.cardId, itemId: null }
            setSelectedItem(null)
            setSelectedId(null)
          }
        }
      } else {
        map.delete(cardId)
      }
      setItemsVersion((version) => version + 1)
    },
    [],
  )

  const refreshCategoriesByIds = React.useCallback(
    async (categoryIds: number[]) => {
      if (!categoryIds.length) {
        return
      }
      const actionsMap = cardActionsRef.current
      const tasks: Array<Promise<unknown>> = []
      for (const card of dashboardRef.current.cards) {
        if (card.type !== 'category' || typeof card.categoryId !== 'number') {
          continue
        }
        if (!categoryIds.includes(card.categoryId)) {
          continue
        }
        const actions = actionsMap.get(card.id)
        if (actions?.refreshItems) {
          tasks.push(actions.refreshItems({ background: true }))
        }
      }
      if (tasks.length) {
        await Promise.allSettled(tasks)
      }
    },
    [],
  )

  const handleOpenCardSettings = React.useCallback(
    (context: DashboardCardSettingsContext) => {
      setSettingsContext(context)
      setSettingsOpenForCardId(context.cardId)
    },
    [],
  )

  const handleCloseCardSettings = React.useCallback(() => {
    setSettingsOpenForCardId(null)
    setSettingsContext(null)
  }, [])

  const handleItemsChange = React.useCallback(
    (cardId: string, _items?: PrepListItem[], _total?: number) => {
      setItemsVersion((version) => version + 1)
      const { cardId: activeCardId, itemId } = selectionRef.current
      if (itemId && activeCardId === cardId) {
        const actions = cardActionsRef.current.get(cardId)
        const updated = actions?.getItemById(itemId) ?? null
        if (updated) {
          setSelectedItem(updated)
        } else if (retainedUsedDrawerIdRef.current !== itemId) {
          selectionRef.current = { cardId: selectionRef.current.cardId, itemId: null }
          setSelectedItem(null)
          setSelectedId(null)
        }
      }
    },
    [],
  )

  const handleSelectItem = React.useCallback((cardId: string, item: PrepListItem) => {
    retainedUsedDrawerIdRef.current = null
    selectionRef.current = { cardId, itemId: item.id }
    setSelectedCardId(cardId)
    setSelectedId(item.id)
    setSelectedItem(item)
  }, [])

  const handleCloseDrawer = React.useCallback(() => {
    retainedUsedDrawerIdRef.current = null
    selectionRef.current = { cardId: null, itemId: null }
    setSelectedId(null)
    setSelectedItem(null)
  }, [])

  const handleActiveItemMissing = React.useCallback(() => {
    if (retainedUsedDrawerIdRef.current === selectionRef.current.itemId) {
      return
    }
    handleCloseDrawer()
  }, [handleCloseDrawer])

  const getCardHandlerBundle = React.useCallback(
    (cardId: string): StoredCardHandlers => {
      const cache = cardHandlerCacheRef.current
      const existing = cache.get(cardId)
      if (existing) {
        return existing
      }

      const handlers: StoredCardHandlers = {
        onCardChange: (next) => updateCardSettings(cardId, next),
        onPartialChange: (patch) => applyCardPatch(cardId, patch),
        onItemsChange: (items, total) => handleItemsChange(cardId, items, total),
        onSelectItem: (item) => handleSelectItem(cardId, item),
        onRegisterActions: (actions) => handleRegisterActions(cardId, actions),
      }

      cache.set(cardId, handlers)
      return handlers
    },
    [applyCardPatch, handleItemsChange, handleRegisterActions, handleSelectItem, updateCardSettings],
  )

  const storedMyPrepCard = React.useMemo(() => dashboard.cards.find((entry) => entry.type === 'my_prep') ?? null, [
    dashboard.cards,
  ])

  const storedMyPrepHandlers = React.useMemo(
    () => (storedMyPrepCard ? getCardHandlerBundle(storedMyPrepCard.id) : null),
    [getCardHandlerBundle, storedMyPrepCard],
  )

  const displayedMyPrepCard = storedMyPrepCard ?? injectedMyPrepCard

  const displayedMyPrepCardHandlers = React.useMemo(() => {
    if (storedMyPrepCard && storedMyPrepHandlers) {
      return {
        onCardChange: storedMyPrepHandlers.onCardChange,
        onRegisterActions: storedMyPrepHandlers.onRegisterActions,
        onSelectItem: storedMyPrepHandlers.onSelectItem,
        onItemsChange: storedMyPrepHandlers.onItemsChange,
        cardId: storedMyPrepCard.id,
      }
    }
    const injectedId = injectedMyPrepCard.id
    return {
      onCardChange: (next: PrepDashboardCard) => setInjectedMyPrepCard(next),
      onRegisterActions: (actions: DashboardCardItemActions | null) => handleRegisterActions(injectedId, actions),
      onSelectItem: (item: PrepListItem) => handleSelectItem(injectedId, item),
      onItemsChange: (items: PrepListItem[], total: number) => handleItemsChange(injectedId, items, total),
      cardId: injectedId,
    }
  }, [
    handleItemsChange,
    handleRegisterActions,
    handleSelectItem,
    injectedMyPrepCard.id,
    storedMyPrepCard,
    storedMyPrepHandlers,
  ])

  React.useEffect(() => {
    if (!storedMyPrepCard) {
      return
    }
    setInjectedMyPrepCard((previous) => {
      if (
        previous.itemsPerPage === storedMyPrepCard.itemsPerPage &&
        previous.collapsed === storedMyPrepCard.collapsed &&
        previous.page === storedMyPrepCard.page
      ) {
        return previous
      }
      return {
        ...previous,
        itemsPerPage: storedMyPrepCard.itemsPerPage,
        collapsed: storedMyPrepCard.collapsed,
        page: storedMyPrepCard.page,
      }
    })
  }, [storedMyPrepCard])

  const categoryCards = React.useMemo(
    () =>
      dashboard.cards.filter((card): card is PrepDashboardCategoryCard =>
        card.type === 'category' && typeof card.categoryId === 'number',
      ),
    [dashboard.cards],
  )

  const categoryEntries = React.useMemo(
    () =>
      categoryCards.map((card) => {
        const handlers = getCardHandlerBundle(card.id)
        return {
          card,
          onChange: handlers.onPartialChange as (patch: Partial<PrepDashboardCategoryCard>) => void,
          onSelectItem: handlers.onSelectItem,
          onRegisterActions: handlers.onRegisterActions,
          onItemsChange: handlers.onItemsChange,
        }
      }),
    [categoryCards, getCardHandlerBundle],
  )

  const categoryEntryMap = React.useMemo(() => {
    const map = new Map<string, (typeof categoryEntries)[number]>()
    categoryEntries.forEach((entry) => {
      map.set(entry.card.id, entry)
    })
    return map
  }, [categoryEntries])

  const gridItems = React.useMemo<PrepDashboardCard[]>(() => {
    const result: PrepDashboardCard[] = []
    const myPrepHandlers = displayedMyPrepCardHandlers

    for (const card of dashboard.cards) {
      if (card.type === 'my_prep') {
        if (!myPrepHandlers) {
          continue
        }
        const resolvedCard =
          displayedMyPrepCard && displayedMyPrepCard.id === card.id
            ? displayedMyPrepCard
            : card
        result.push(resolvedCard)
      } else if (card.type === 'category') {
        const entry = categoryEntryMap.get(card.id)
        if (!entry) {
          continue
        }
        result.push(entry.card)
      }
    }

    if (result.length === 0 && displayedMyPrepCardHandlers) {
      result.push(displayedMyPrepCard)
    }

    return result
  }, [
    categoryEntryMap,
    dashboard.cards,
    displayedMyPrepCard,
    displayedMyPrepCardHandlers,
  ])

  const snapshotIncludeImages = React.useMemo(
    () => listShowImages || gridItems.some((card) => card.listShowImages === true),
    [gridItems, listShowImages],
  )

  const requestDashboardSnapshot = React.useCallback(
    async (background = false, force = false) => {
      if (!optimizedDashboardEnabled || !stationId || gridItems.length === 0) {
        return
      }
      await loadDashboardSnapshot(
        {
          userId: me.user.id,
          stationId,
          ageMaxDays,
          dateBasis,
          includeImages: snapshotIncludeImages,
          cards: gridItems,
        },
        { background, force },
      )
    },
    [ageMaxDays, dateBasis, gridItems, loadDashboardSnapshot, me.user.id, optimizedDashboardEnabled, snapshotIncludeImages, stationId],
  )

  const revalidateDashboardOnEntryRef = React.useRef(() => {
    const snapshotState = usePrepDashboardStore.getState()
    return snapshotState.snapshotDirty || snapshotState.myPrepDirty
  })
  React.useEffect(() => {
    if (!optimizedDashboardEnabled || !stationId || gridItems.length === 0) {
      return
    }
    const forceDashboardRefresh = revalidateDashboardOnEntryRef.current()
    void requestDashboardSnapshot(forceDashboardRefresh, forceDashboardRefresh).catch(() => undefined)
  }, [gridItems.length, optimizedDashboardEnabled, requestDashboardSnapshot, stationId])

  const refreshDashboardSnapshot = React.useCallback(
    () => requestDashboardSnapshot(true, true),
    [requestDashboardSnapshot],
  )

  const myPrepRefreshControllerRef = React.useRef<AbortController | null>(null)
  React.useEffect(
    () => () => {
      myPrepRefreshControllerRef.current?.abort()
      myPrepRefreshControllerRef.current = null
    },
    [me.user.id, stationId],
  )

  const refreshMyPrepCard = React.useCallback(async (expected?: {
    itemId: number
    inMyPrep: boolean
  }) => {
    if (!optimizedDashboardEnabled || !displayedMyPrepCard) {
      return
    }

    myPrepRefreshControllerRef.current?.abort()
    const controller = new AbortController()
    myPrepRefreshControllerRef.current = controller
    const optimisticCard = usePrepDashboardStore.getState().cardsById[displayedMyPrepCard.id]
    const optimisticTotal = optimisticCard?.total ?? null

    try {
      const response = await getMyPrepList({
        page: displayedMyPrepCard.page,
        limit: displayedMyPrepCard.itemsPerPage,
        idsOnly: false,
        signal: controller.signal,
      })
      const currentState = usePrepDashboardStore.getState()
      if (controller.signal.aborted || currentState.stationId !== stationId) {
        return
      }
      if (expected) {
        const currentHasMembership = currentState.myPrepIds.includes(expected.itemId)
        if (currentHasMembership !== expected.inMyPrep || response.total !== optimisticTotal) {
          return
        }
      }
      usePrepDashboardStore.getState().replaceMyPrepCardPage({
        cardId: displayedMyPrepCard.id,
        items: response.items,
        total: response.total,
        page: response.page,
        perPage: response.per_page,
      })
    } catch (error) {
      if (controller.signal.aborted) {
        return
      }
      console.error('[prep] Unable to refresh the My Prep dashboard card', error)
    } finally {
      if (myPrepRefreshControllerRef.current === controller) {
        myPrepRefreshControllerRef.current = null
      }
    }
  }, [displayedMyPrepCard, optimizedDashboardEnabled, stationId])

  usePrepDashboardRevisionPoll({
    stationId,
    enabled: optimizedDashboardEnabled,
    onRevisionChanged: refreshDashboardSnapshot,
  })

  const loadDelayByCardId = React.useMemo(() => {
    const resolvedColumns = Math.min(4, Math.max(1, Math.floor(dashboard.columns || 1)))
    const normalized = gridItems.map((item, idx) => {
      const normalizedColumn = (() => {
        const col = (item as { column?: number }).column
        if (typeof col === 'number' && Number.isFinite(col) && col >= 1) {
          return Math.min(resolvedColumns, Math.max(1, Math.floor(col)))
        }
        return (idx % resolvedColumns) + 1
      })()

      const normalizedOrder = (() => {
        const ord = (item as { columnOrder?: number }).columnOrder
        if (typeof ord === 'number' && Number.isFinite(ord) && ord >= 1) {
          return Math.floor(ord)
        }
        return Number.MAX_SAFE_INTEGER - (gridItems.length - idx)
      })()

      return { item, column: normalizedColumn, order: normalizedOrder, idx }
    })

    const columns: Array<typeof normalized> = Array.from({ length: resolvedColumns }, () => [])
    normalized.forEach((entry) => {
      columns[entry.column - 1]?.push(entry)
    })
    columns.forEach((col) => col.sort((a, b) => (a.order === b.order ? a.idx - b.idx : a.order - b.order)))

    const sequence: PrepDashboardCard[] = []
    const maxRows = Math.max(...columns.map((col) => col.length))
    for (let row = 0; row < maxRows; row += 1) {
      for (let col = 0; col < resolvedColumns; col += 1) {
        const entry = columns[col]?.[row]
        if (entry) {
          sequence.push(entry.item)
        }
      }
    }

    const baseDelay = 140
    const delayMap = new Map<string, number>()
    sequence.forEach((card, idx) => {
      delayMap.set(card.id, idx * baseDelay)
    })

    return delayMap
  }, [dashboard.columns, gridItems])

  const renderGridItem = React.useCallback(
    (card: PrepDashboardCard) => {
      const renderCard = (snapshotData?: DashboardSnapshotData) => {
        if (card.type === 'my_prep') {
          if (!displayedMyPrepCardHandlers) {
            return null
          }
          const handlers = displayedMyPrepCardHandlers
          const resolvedCard =
            displayedMyPrepCard && displayedMyPrepCard.id === card.id
              ? displayedMyPrepCard
              : card
          return (
            <MyPrepCard
              card={resolvedCard}
              onCardChange={handlers.onCardChange}
              onRegisterActions={handlers.onRegisterActions}
              onSelectItem={handlers.onSelectItem}
              onItemsChange={handlers.onItemsChange}
              activeItemId={
                selectedCardId === resolvedCard.id ? selectedId ?? undefined : undefined
              }
              onActiveItemMissing={
                selectedCardId === resolvedCard.id ? handleActiveItemMissing : undefined
              }
              isSuperadmin={isSuperadmin}
              onOpenSettings={handleOpenCardSettings}
              onRefreshCategories={refreshCategoriesByIds}
              snapshotData={snapshotData}
              onRequestMyPrepRefresh={optimizedDashboardEnabled ? refreshMyPrepCard : undefined}
            />
          )
        }

        if (card.type === 'category') {
          const entry = categoryEntryMap.get(card.id)
          if (!entry) {
            return null
          }
          return (
            <CategoryCard
              card={entry.card}
              onChange={entry.onChange}
              onSelectItem={entry.onSelectItem}
              onRegisterActions={entry.onRegisterActions}
              onItemsChange={entry.onItemsChange}
              activeItemId={selectedCardId === card.id ? selectedId ?? undefined : undefined}
              onActiveItemMissing={
                selectedCardId === card.id ? handleActiveItemMissing : undefined
              }
              isSuperadmin={isSuperadmin}
              onOpenSettings={handleOpenCardSettings}
              onRefreshCategories={refreshCategoriesByIds}
              initialDelayMs={loadDelayByCardId.get(entry.card.id) ?? 0}
              snapshotData={snapshotData}
              onRequestSnapshotRefresh={optimizedDashboardEnabled ? refreshDashboardSnapshot : undefined}
              onRequestMyPrepRefresh={optimizedDashboardEnabled ? refreshMyPrepCard : undefined}
            />
          )
        }

        return null
      }

      if (!optimizedDashboardEnabled) {
        return renderCard()
      }

      return (
        <DashboardSnapshotConnector cardId={card.id}>
          {(snapshotData) => renderCard(snapshotData)}
        </DashboardSnapshotConnector>
      )
    },
    [
      categoryEntryMap,
      displayedMyPrepCard,
      displayedMyPrepCardHandlers,
      handleActiveItemMissing,
      handleOpenCardSettings,
      isSuperadmin,
      loadDelayByCardId,
      optimizedDashboardEnabled,
      refreshDashboardSnapshot,
      refreshMyPrepCard,
      selectedCardId,
      selectedId,
      refreshCategoriesByIds,
    ],
  )

  const getGridItemTitle = React.useCallback(
    (card: PrepDashboardCard) => {
      if (card.type === 'my_prep') {
        return 'My Prep Items'
      }
      if (card.type === 'category') {
        return (
          snapshotCardsById[card.id]?.category_name?.trim() ||
          categoryEntryMap.get(card.id)?.card.categoryName?.trim() ||
          card.categoryName?.trim() ||
          'Prep category'
        )
      }
      return 'Prep card'
    },
    [categoryEntryMap, snapshotCardsById],
  )

  const handleLayoutChange = React.useCallback(
    (_event: React.SyntheticEvent<Element, Event>, next: PrepLayout | null) => {
      if (!next || next === layout) {
        return
      }
      if (next === 'single' && !isMdUp) {
        return
      }
      setPrepLayout(next)
    },
    [isMdUp, layout, setPrepLayout],
  )

  const handleRecoveryToggle = React.useCallback(() => {
    setRecoveryOpen(!recoveryOpen)
  }, [recoveryOpen, setRecoveryOpen])

  const loadDetail = React.useCallback(async (id: number, signal?: AbortSignal) => {
    if (detailCacheRef.current.has(id)) {
      return detailCacheRef.current.get(id) as PrepDetailResponse
    }
    const detail = await fetchPrepItemDetail(id, { signal })
    cacheDetail(detail)
    return detail
  }, [cacheDetail])

  const handleToggleMyPrep = React.useCallback(
    async (next: boolean) => {
      if (!selectedId || !selectedCardId) {
        return
      }
      const actions = cardActionsRef.current.get(selectedCardId)
      if (!actions) {
        return
      }
      await actions.toggleMyPrep(selectedId, next)
      const updated = actions.getItemById(selectedId) ?? null
      if (updated) {
        setSelectedItem(updated)
      } else {
        handleCloseDrawer()
      }
    },
    [handleCloseDrawer, selectedCardId, selectedId],
  )

  const handleDismiss = React.useCallback(async () => {
    if (!selectedId || !selectedCardId) {
      return
    }
    const actions = cardActionsRef.current.get(selectedCardId)
    if (!actions) {
      return
    }
    await actions.dismissForMe(selectedId)
    const updated = actions.getItemById(selectedId) ?? null
    if (updated) {
      setSelectedItem(updated)
    } else {
      handleCloseDrawer()
    }
  }, [handleCloseDrawer, selectedCardId, selectedId])

  const handleMarkUsed = React.useCallback(async () => {
    if (!selectedId || !selectedCardId) {
      return
    }
    const actions = cardActionsRef.current.get(selectedCardId)
    if (!actions) {
      return
    }

    retainedUsedDrawerIdRef.current = selectedId
    try {
      await actions.markUsedForMe(selectedId)
      setSelectedItem((current) =>
        current && current.id === selectedId ? { ...current, is_in_my_prep: false } : current,
      )
    } catch (error) {
      retainedUsedDrawerIdRef.current = null
      throw error
    }
  }, [selectedCardId, selectedId])

  const handleArchive = React.useCallback(async () => {
    if (!selectedId || !selectedCardId) {
      return
    }
    const actions = cardActionsRef.current.get(selectedCardId)
    if (!actions) {
      return
    }
    const item = actions.getItemById(selectedId)
    if (!item) {
      return
    }
    if (!actions.dismissForAll) {
      return
    }
    await actions.dismissForAll(selectedId)
    const updated = actions.getItemById(selectedId) ?? null
    if (updated) {
      setSelectedItem(updated)
    } else if (retainedUsedDrawerIdRef.current !== selectedId) {
      handleCloseDrawer()
    }
  }, [handleCloseDrawer, selectedCardId, selectedId])

  React.useEffect(() => {
    if (!selectedId || !selectedCardId) {
      return
    }
    const actions = cardActionsRef.current.get(selectedCardId)
    if (!actions) {
      return
    }
    const updated = actions.getItemById(selectedId) ?? null
    if (updated) {
      setSelectedItem(updated)
    } else if (retainedUsedDrawerIdRef.current !== selectedId) {
      handleCloseDrawer()
    }
  }, [handleCloseDrawer, itemsVersion, selectedCardId, selectedId])

  const cachedDetail = selectedId ? detailCacheRef.current.get(selectedId) ?? null : null
  const drawerOpen = Boolean(selectedId)

  const handleSaveCardSettings = React.useCallback(
    async (
      cardId: string,
      options: {
        itemsPerPage: number
        headlineLines?: 1 | 2 | 3 | null
        listShowImages?: boolean | null
        column?: number | null
        columnOrder?: number | null
      },
    ): Promise<boolean> => {
      const current = dashboardRef.current
      const cardIndex = current.cards.findIndex((entry) => entry.id === cardId)
      if (cardIndex === -1) {
        return false
      }
      const normalizeColumnCount = (value: number | undefined): number =>
        Math.min(4, Math.max(1, Math.floor(Number.isFinite(value) ? value : 1)))

      const reflowCards = (
        cards: PrepDashboardCard[],
        movedCardId: string,
        targetColumn: number | null | undefined,
        targetOrder: number | null | undefined,
        columnCount: number,
      ): PrepDashboardCard[] => {
        const resolvedColumns = normalizeColumnCount(columnCount || 1)
        const buckets: Array<
          Array<{ card: PrepDashboardCard; column: number; order: number; originalIdx: number }>
        > = Array.from({ length: resolvedColumns }, () => [])

        let moving:
          | { card: PrepDashboardCard; column: number; order: number; originalIdx: number }
          | null = null

        cards.forEach((card, idx) => {
          const resolvedColumn = (() => {
            const rawColumn = card.column
            if (typeof rawColumn === 'number' && Number.isFinite(rawColumn) && rawColumn >= 1) {
              return Math.min(resolvedColumns, Math.max(1, Math.floor(rawColumn)))
            }
            return (idx % resolvedColumns) + 1
          })()

          const fallbackOrder = buckets[resolvedColumn - 1].length + 1
          const resolvedOrder =
            typeof card.columnOrder === 'number' &&
            Number.isFinite(card.columnOrder) &&
            card.columnOrder >= 1
              ? Math.floor(card.columnOrder)
              : fallbackOrder

          const entry = { card, column: resolvedColumn, order: resolvedOrder, originalIdx: idx }

          if (card.id === movedCardId) {
            moving = entry
            return
          }

          buckets[resolvedColumn - 1].push(entry)
        })

        if (!moving) {
          return cards
        }

        // Sort existing buckets to stabilize current ordering.
        const sortedBuckets = buckets.map((bucket) =>
          bucket.slice().sort((a, b) => {
            if (a.order === b.order) {
              return a.originalIdx - b.originalIdx
            }
            return a.order - b.order
          }),
        )

        const normalizedTargetColumn =
          typeof targetColumn === 'number' && Number.isFinite(targetColumn)
            ? Math.min(resolvedColumns, Math.max(1, Math.floor(targetColumn)))
            : moving.column

        const targetBucket = sortedBuckets[normalizedTargetColumn - 1]
        const desiredOrder =
          typeof targetOrder === 'number' && Number.isFinite(targetOrder)
            ? Math.max(1, Math.floor(targetOrder))
            : moving.order
        const insertIndex = Math.min(Math.max(desiredOrder - 1, 0), targetBucket.length)

        targetBucket.splice(insertIndex, 0, {
          ...moving,
          column: normalizedTargetColumn,
          order: desiredOrder,
        })

        const nextCards: PrepDashboardCard[] = []

        for (let col = 1; col <= resolvedColumns; col += 1) {
          const bucket = sortedBuckets[col - 1]
          bucket.forEach((entry, idxInBucket) => {
            nextCards.push({
              ...entry.card,
              column: col,
              columnOrder: idxInBucket + 1,
            })
          })
        }

        return nextCards
      }

      const existing = current.cards[cardIndex]
      const nextCard: PrepDashboardCard = {
        ...existing,
        itemsPerPage: clampItemsPerPage(options.itemsPerPage),
        ...(options.headlineLines !== undefined
          ? { headlineLines: options.headlineLines ?? undefined }
          : {}),
        ...(options.listShowImages !== undefined
          ? { listShowImages: options.listShowImages ?? undefined }
          : {}),
        ...(options.column !== undefined ? { column: options.column ?? undefined } : {}),
        ...(options.columnOrder !== undefined ? { columnOrder: options.columnOrder ?? undefined } : {}),
      }
      const updatedCards = current.cards.map((entry, index) => (index === cardIndex ? nextCard : entry))
      const rebalancedCards = reflowCards(
        updatedCards,
        cardId,
        options.column ?? nextCard.column ?? null,
        options.columnOrder ?? nextCard.columnOrder ?? null,
        current.columns,
      )
      const nextDashboard = { ...current, cards: rebalancedCards }

      const unchanged =
        nextDashboard.cards.length === current.cards.length &&
        nextDashboard.cards.every((card, idx) => {
          const prev = current.cards[idx]
          if (!prev || prev.id !== card.id) {
            return false
          }
          return (
            prev.itemsPerPage === card.itemsPerPage &&
            prev.headlineLines === card.headlineLines &&
            prev.listShowImages === card.listShowImages &&
            prev.column === card.column &&
            prev.columnOrder === card.columnOrder
          )
        })

      if (unchanged) {
        return false
      }

      setPrepDashboard(nextDashboard, { persist: false })

      try {
        const response = await updateUserSettings({ prepDashboard: nextDashboard })
        if (response.prepDashboard) {
          setPrepDashboard(response.prepDashboard, { persist: false })
        }
        setSnackbar({ message: 'Card settings saved.', severity: 'success' })
        return true
      } catch (error) {
        setPrepDashboard(current, { persist: false })
        setSnackbar({
          message: 'Could not save dashboard changes. Please try again.',
          severity: 'error',
        })
        throw error
      }
    },
    [setPrepDashboard],
  )

  const handleRemoveCard = React.useCallback(
    async (cardId: string) => {
      const current = dashboardRef.current
      const cardIndex = current.cards.findIndex((entry) => entry.id === cardId)
      if (cardIndex === -1) {
        return
      }
      const nextCards = current.cards.filter((entry) => entry.id !== cardId)
      const nextDashboard = { ...current, cards: nextCards }
      const previousSelection = {
        cardId: selectedCardId,
        id: selectedId,
        item: selectedItem,
      }

      if (selectedCardId === cardId) {
        setSelectedCardId(null)
        setSelectedId(null)
        setSelectedItem(null)
      }

      setPrepDashboard(nextDashboard, { persist: false })

      try {
        const response = await updateUserSettings({ prepDashboard: nextDashboard })
        if (response.prepDashboard) {
          setPrepDashboard(response.prepDashboard, { persist: false })
        }
        setSnackbar({ message: 'Card removed.', severity: 'success' })
      } catch (error) {
        setPrepDashboard(current, { persist: false })
        if (previousSelection.cardId) {
          setSelectedCardId(previousSelection.cardId)
          setSelectedId(previousSelection.id)
          setSelectedItem(previousSelection.item)
        }
        setSnackbar({
          message: 'Could not save dashboard changes. Please try again.',
          severity: 'error',
        })
        throw error
      }
    },
    [selectedCardId, selectedId, selectedItem, setPrepDashboard],
  )

  const addDashboardCategoryCards = useUserSettingsStore((state) => state.addDashboardCategoryCards)

  const handleAddCategoryCards = React.useCallback(
    async (cards: Array<{ categoryId: number; itemsPerPage: number; collapsed?: boolean }>) => {
      if (!cards.length) {
        return
      }

      const currentDashboard = dashboardRef.current
      const existingCategoryIds = new Set<number>()
      for (const card of currentDashboard.cards) {
        if (card.type === 'category' && typeof card.categoryId === 'number') {
          existingCategoryIds.add(card.categoryId)
        }
      }

      const deduped: Array<{ categoryId: number; itemsPerPage: number; collapsed?: boolean }> = []
      let skipped = 0

      for (const card of cards) {
        if (existingCategoryIds.has(card.categoryId)) {
          skipped += 1
          continue
        }
        existingCategoryIds.add(card.categoryId)
        deduped.push(card)
      }

      if (deduped.length === 0) {
        if (skipped > 0) {
          setSnackbar({
            message:
              skipped === 1
                ? 'That category is already on your dashboard.'
                : 'Those categories are already on your dashboard.',
            severity: 'info',
          })
        }
        return
      }

      try {
        await addDashboardCategoryCards(deduped)
        setSnackbar({
          message: `Added ${deduped.length} card${deduped.length === 1 ? '' : 's'}.`,
          severity: 'success',
        })
      } catch (error) {
        setSnackbar({
          message: 'Could not save dashboard changes. Please try again.',
          severity: 'error',
        })
        throw error
      }
    },
    [addDashboardCategoryCards],
  )

  const activeSettingsCard = React.useMemo(() => {
    if (!settingsOpenForCardId) {
      return null
    }
    return (
      dashboard.cards.find((card) => card.id === settingsOpenForCardId) ??
      (settingsOpenForCardId === displayedMyPrepCard.id ? displayedMyPrepCard : null)
    )
  }, [dashboard.cards, displayedMyPrepCard, settingsOpenForCardId])

  const handleSaveActiveCardSettings = React.useCallback(
    async ({
      itemsPerPage,
      headlineLines,
      listShowImages,
      column,
      columnOrder,
    }: {
      itemsPerPage: number
      headlineLines?: 1 | 2 | 3 | null
      listShowImages?: boolean | null
      column?: number | null
      columnOrder?: number | null
    }) => {
      if (!activeSettingsCard) {
        return
      }
      const cardId = activeSettingsCard.id
      await handleSaveCardSettings(cardId, {
        itemsPerPage,
        headlineLines,
        listShowImages,
        column,
        columnOrder,
      })
    },
    [
      activeSettingsCard,
      handleSaveCardSettings,
    ],
  )

  const handleRemoveActiveCard = React.useCallback(() => {
    if (!activeSettingsCard) {
      return Promise.resolve()
    }
    return Promise.resolve(handleRemoveCard(activeSettingsCard.id))
  }, [activeSettingsCard, handleRemoveCard])

  const handleAgeMaxDaysChange = React.useCallback(
    (value: PrepAgeMaxDays) => {
      setPrepAgeMaxDays(value)
    },
    [setPrepAgeMaxDays],
  )

  const handleDateBasisChange = React.useCallback(
    (basis: PrepDateBasis) => {
      setPrepDateBasis(basis)
      setPrepDashboard(
        {
          ...dashboard,
          cards: dashboard.cards.map((card) => ({ ...card, page: 1 })),
        },
        { persist: false },
      )
    },
    [dashboard, setPrepDashboard, setPrepDateBasis],
  )

  const handleCloseSnackbar = React.useCallback(() => {
    setSnackbar(null)
  }, [])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
        gap: { xs: 1.5, md: 2 },
        px: { xs: 2, md: 3 },
        py: { xs: 1.5, md: 2 },
        boxSizing: 'border-box',
        overflowX: 'clip',
      }}
    >
      <AddPrepModal
        open={addPrepOpen}
        categories={modalCategories}
        onClose={() => setAddPrepOpen(false)}
        onSubmitSuccess={handleAddPrepSuccess}
        onSubmitError={handleAddPrepError}
      />
      <PrepDashboardSettingsModal
        open={dashboardSettingsOpen}
        onClose={() => setDashboardSettingsOpen(false)}
        existingCards={dashboard.cards}
        onAddCategoryCards={handleAddCategoryCards}
      />
      <DashboardCardSettingsDialog
        open={Boolean(settingsOpenForCardId)}
        card={activeSettingsCard}
        context={settingsContext}
        onClose={handleCloseCardSettings}
        onSave={handleSaveActiveCardSettings}
        onRemove={handleRemoveActiveCard}
        columns={dashboard.columns}
        cards={dashboard.cards}
      />
      <Box
        className="rpa-prepHeaderBar"
        sx={{ flex: '0 0 auto', py: { xs: 0.1, md: 0.2 }, mb: { xs: 0.1, md: 0.2 } }}
      >
        <Stack direction="row" justifyContent="flex-start" sx={{ alignItems: 'center', gap: 1.5 }}>
          <Tooltip title="Dashboard settings">
            <IconButton
              size="small"
              edge="start"
              aria-label="Dashboard settings"
              onClick={() => setDashboardSettingsOpen(true)}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add">
            <span>
              <IconButton
                size="small"
                aria-label="Add"
                onClick={() => setAddPrepOpen(true)}
                sx={{ minHeight: 32, minWidth: 32 }}
              >
                <AddCircleOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <MyPrepToggleButton />
          <Tooltip title={recoveryOpen ? 'Close used & dismissed view' : 'Show Used & Dismissed Prep Items'}>
            <span>
              {recoveryOpen ? (
                <Button
                  size="small"
                  aria-label="Close used and dismissed prep items"
                  onClick={handleRecoveryToggle}
                  color="error"
                  variant="contained"
                  aria-pressed={recoveryOpen}
                  startIcon={<DeleteOutlineIcon fontSize="small" />}
                  sx={{ minHeight: 32, borderRadius: 999, textTransform: 'none' }}
                >
                  Close
                </Button>
              ) : (
                <IconButton
                  size="small"
                  aria-label="Show used and dismissed prep items"
                  onClick={handleRecoveryToggle}
                  color="default"
                  aria-pressed={recoveryOpen}
                  sx={{ minHeight: 32, minWidth: 32 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </span>
          </Tooltip>
          {layout === 'dashboard' ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Expand all cards">
                <Button
                  size="small"
                  startIcon={<UnfoldMoreIcon fontSize="small" />}
                  aria-label="Expand all cards"
                  onClick={handleExpandAllCards}
                >
                  Expand all
                </Button>
              </Tooltip>
              <Tooltip title="Collapse all cards">
                <Button
                  size="small"
                  startIcon={<UnfoldLessIcon fontSize="small" />}
                  aria-label="Collapse all cards"
                  onClick={handleCollapseAllCards}
                >
                  Collapse all
                </Button>
              </Tooltip>
            </Box>
          ) : null}
          <ToggleButtonGroup
            exclusive
            size="small"
            value={layout}
            onChange={handleLayoutChange}
            aria-label="Layout"
            sx={{
              '& .MuiToggleButton-root': {
                px: 1,
                fontWeight: 500,
              },
            }}
          >
            <LayoutToggleButton
              value="dashboard"
              aria-label="Dashboard layout"
              tooltip="Dashboard layout"
              tooltipProps={{ enterDelay: 300 }}
            >
              <DashboardIcon fontSize="small" />
            </LayoutToggleButton>
            <LayoutToggleButton
              value="columns"
              aria-label="Columns layout"
              tooltip="Columns layout"
              tooltipProps={{ enterDelay: 300 }}
            >
              <ViewColumnIcon fontSize="small" />
            </LayoutToggleButton>
            <LayoutToggleButton
              value="single"
              aria-label="Single layout"
              tooltip={
                isMdUp ? 'Single layout' : 'Single layout is available on wider screens'
              }
              tooltipProps={{ enterDelay: 300 }}
              disabled={!isMdUp}
            >
              <LooksOneIcon fontSize="small" />
            </LayoutToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Default lines per card (cards can override)" enterDelay={300}>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={headlineLines}
                onChange={(_event, value: 1 | 2 | 3 | null) => {
                  if (value !== null) {
                    setPrepHeadlineLines(value)
                  }
                }}
                aria-label="Default lines per item"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 0.75,
                    fontWeight: 500,
                  },
                }}
              >
                <ToggleButton value={1} aria-label="1 line per item">
                  1
                </ToggleButton>
                <ToggleButton value={2} aria-label="2 lines per item">
                  2
                </ToggleButton>
                <ToggleButton value={3} aria-label="3 lines per item">
                  3
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Tooltip>

          <Tooltip title="Images default (cards can override)" enterDelay={300}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={Boolean(listShowImages)}
                  onChange={(event) => setListShowImages(event.target.checked)}
                  inputProps={{ 'aria-label': 'Toggle images default' }}
                />
              }
              label="Images"
              sx={{
                m: 0,
                ml: 0.5,
                mr: 0,
                '& .MuiFormControlLabel-label': { fontWeight: 500 },
              }}
            />
          </Tooltip>

          <PrepDateFilters
            dateBasis={dateBasis}
            onDateBasisChange={handleDateBasisChange}
            ageMaxDays={ageMaxDays}
            onAgeMaxDaysChange={handleAgeMaxDaysChange}
          />
        </Stack>
      </Box>
      {optimizedDashboardEnabled && snapshotSource === 'cache' && snapshotRefreshing ? (
        <Alert severity="info" variant="outlined" sx={{ flex: '0 0 auto' }}>
          Showing your recent session snapshot while Prep updates in the background.
        </Alert>
      ) : null}
      {optimizedDashboardEnabled && snapshotStale && snapshotError ? (
        <Alert
          severity="warning"
          variant="outlined"
          action={
            <Button color="inherit" size="small" onClick={() => void refreshDashboardSnapshot().catch(() => undefined)}>
              Retry
            </Button>
          }
          sx={{ flex: '0 0 auto' }}
        >
          Prep could not update, so the current items remain available. Automatic checks will continue.
        </Alert>
      ) : null}
      <DashboardGrid
        columns={dashboard.columns}
        items={gridItems}
        renderItem={renderGridItem}
        virtualize={optimizedDashboardEnabled}
        getItemTitle={getGridItemTitle}
      />
      <PrepDetailDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        prepId={selectedId ?? null}
        cached={cachedDetail}
        fallbackItem={selectedItem}
        onToggleMyPrep={handleToggleMyPrep}
        onDismiss={handleDismiss}
        onMarkUsed={handleMarkUsed}
        onArchive={handleArchive}
        onAddToShowClock={handleOpenAddToShowClock}
        launchedFromMyPrep={false}
        isSuperadmin={isSuperadmin}
        onDetailLoaded={(detail) => {
          cacheDetail(detail)
        }}
        onRefreshCategories={refreshCategoriesByIds}
      />

      <AddPrepToShowClockModal
        open={Boolean(addToShowClockTarget)}
        stationId={stationId}
        prepItemId={addToShowClockTarget?.prepId ?? 0}
        prepHeadline={addToShowClockTarget?.prepHeadline ?? 'Prep item'}
        launchedFromMyPrep={addToShowClockTarget?.launchedFromMyPrep ?? false}
        onClose={() => setAddToShowClockTarget(null)}
        onSuccessToast={(message) => setSnackbar({ message, severity: 'success' })}
      />
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  )
}

export default PrepDashboardPage
